import { EntityType } from '@frontend/common';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { EquipmentClient } from '@frontend/equipment/api';
import { EquipmentStateName, IEquipment } from '@frontend/equipment/types';
import { Application, Feature } from '@frontend/functionality/types';
import { IdentificationClient } from '@frontend/identification/api';
import { CommonMessage } from '@frontend/lang';
import { LaundryEquipmentClient } from '@frontend/laundry-app/api';
import { LaundryEquipment } from '@frontend/laundry-app/types';
import { ModalType } from '@frontend/modal';
import { CellContext } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import OverviewHeader from '../../header/overview-header';
import { TabFilter } from '../../tab-filter/tab-filter.component';
import { CreateItemModal } from '../create-item-modal/create-item-modal';
import { useItems } from './items.controller';

export const Items = () => {
    const viewProps = useItems();
    return (
        <div
            key={'laundry-items-list'}
            className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader
                title={
                    <FormattedMessage
                        id='laundry.items.title'
                        defaultMessage='Items'
                    />
                }
                create={{
                    onCreate: () =>
                        viewProps.openModal(
                            'pudo-create-transaction',
                            () => <CreateItemModal formId={viewProps.formId} />,
                            {},
                            { type: ModalType.PANE, className: 'max-w-[600px]' }
                        ),
                    entity: CommonMessage.OBJECTS.COMMON_FIELDS.ITEM,
                    overwriteCreateLabel: CommonMessage.FORMS.CREATE_OBJECT(CommonMessage.OBJECTS.COMMON_FIELDS.ITEM)
                }}
            />
            <div className='inline-flex flex-col items-start justify-start gap-4 self-stretch'>
                <TabFilter
                    keys={[
                        'in_use',
                        'in_stock',
                        'at_laundry'
                        //'with_problems'
                    ]}
                    displayMap={
                        new Map([
                            ['in_use', 'In use'],
                            ['in_stock', 'In stock'],
                            ['at_laundry', 'At laundry']
                            //['with_problems', 'With problems']
                        ])
                    }
                    onChange={viewProps.setActiveTab}
                    useQueryParams={{ key: 'tab' }}
                />
                <ItemsList
                    key={viewProps.activeTab}
                    {...viewProps}
                />
            </div>
        </div>
    );
};

const ItemsList = (viewProps: ReturnType<typeof useItems>) => {
    const { openPlatformDetail } = usePlatformModalHost();
    switch (viewProps.activeTab) {
        case 'in_use':
            return (
                <ReactiveObjectList<LaundryEquipment>
                    fetch={async (arg) => {
                        return LaundryEquipmentClient.fetchLaundryEquipments(viewProps.currentAccount, { state: EquipmentStateName.EQUIPMENT_IN_USE, ...arg });
                    }}
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({
                            entityType: EntityType.EQUIPMENT,
                            id: obj.id,
                            context: { application: Application.LAUNDRY, feature: Feature.EQUIPMENTS }
                        })
                    }
                    id='laundry-items-list-in-use'
                    dispatch={viewProps.dispatch}
                    activeFilter={{
                        searchKeys: ['tag', 'serial_number', 'product', 'owner'],
                        filterKeys: ['owners', 'products'],
                        displayName: new Map([
                            ['serial_number', CommonMessage.OBJECTS.EQUIPMENT.SERIAL_NUMBER],
                            ['product', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                            ['products', CommonMessage.OBJECTS.PRODUCT.PLURAL],
                            ['owner', CommonMessage.OBJECTS.PRODUCT.OWNER],
                            ['owners', CommonMessage.OBJECTS.PRODUCT.OWNERS]
                        ]),
                        queryKeyMap: new Map([
                            ['owners', 'owner_id'],
                            ['products', 'product_id']
                        ])
                    }}
                    columns={{
                        columns: ['tag', 'serial_number', 'product', 'categories', 'owner'],
                        headerOverwrite: new Map([
                            ['serial_number', CommonMessage.OBJECTS.EQUIPMENT.SERIAL_NUMBER],
                            ['product', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                            ['categories', CommonMessage.OBJECTS.CATEGORY.DEFAULT],
                            ['owner', CommonMessage.OBJECTS.PRODUCT.OWNER]
                        ]),
                        customCells: new Map([
                            ['tag', (obj) => <td className={defaultCellClassName}>{obj.row.original.tag?.name}</td>],
                            [
                                'product',
                                (obj) => (
                                    <td className={defaultCellClassName}>
                                        <PlatformObjectBadge
                                            entityType={EntityType.PRODUCT}
                                            display={obj.row.original.product.name}
                                            id={obj.row.original.product.entity_id}
                                            foreign={obj.row.original.product.foreign}
                                            accountId={accountIdFromResolvedEntity(obj.row.original.product)}
                                        />
                                    </td>
                                )
                            ],
                            [
                                'categories',
                                (obj) => (
                                    <td className={defaultCellClassName}>
                                        {obj.row.original.categories?.length > 0 && (
                                            <>{obj.row.original.categories[0].name}</>
                                            // <PlatformObjectBadge
                                            //     entityType={EntityType.CATEGORY}
                                            //     display={obj.row.original.categories[0].name}
                                            //     id={obj.row.original.categories[0].entity_id}
                                            // />
                                        )}
                                    </td>
                                )
                            ],
                            [
                                'owner',
                                (obj) => (
                                    <td className={defaultCellClassName}>
                                        {obj.row.original.user && (
                                            <PlatformObjectBadge
                                                entityType={EntityType.USER}
                                                display={obj.row.original.user.name}
                                                id={obj.row.original.user.entity_id}
                                                key={obj.row.original.user.entity_id}
                                                foreign={obj.row.original.user.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.user)}
                                            />
                                        )}
                                    </td>
                                )
                            ]
                        ])
                    }}
                    type={EntityType.EQUIPMENT}
                />
            );
        case 'in_stock':
            return (
                <ReactiveObjectList<LaundryEquipment>
                    fetch={(arg) =>
                        LaundryEquipmentClient.fetchLaundryEquipments(viewProps.currentAccount, { state: EquipmentStateName.EQUIPMENT_CREATED, ...arg })
                    }
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({
                            entityType: EntityType.EQUIPMENT,
                            id: obj.id,
                            context: { application: Application.LAUNDRY, feature: Feature.EQUIPMENTS }
                        })
                    }
                    id='laundry-items-list-in-stock'
                    dispatch={viewProps.dispatch}
                    activeFilter={{
                        searchKeys: ['tag', 'serial_number', 'product', 'categories'],
                        displayName: new Map([
                            ['serial_number', CommonMessage.OBJECTS.EQUIPMENT.SERIAL_NUMBER],
                            ['product', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                            ['categories', CommonMessage.OBJECTS.CATEGORY.DEFAULT]
                        ])
                    }}
                    columns={{
                        columns: ['tag', 'serial_number', 'product', 'categories'],
                        headerOverwrite: new Map([
                            ['serial_number', CommonMessage.OBJECTS.EQUIPMENT.SERIAL_NUMBER],
                            ['product', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                            ['categories', CommonMessage.OBJECTS.CATEGORY.DEFAULT]
                        ]),
                        customCells: new Map([
                            ['tag', (obj) => <td className={defaultCellClassName}>{obj.row.original.tag?.name}</td>],
                            [
                                'product',
                                (obj) => (
                                    <td className={defaultCellClassName}>
                                        <PlatformObjectBadge
                                            entityType={EntityType.PRODUCT}
                                            display={obj.row.original.product.name}
                                            id={obj.row.original.product.entity_id}
                                            foreign={obj.row.original.product.foreign}
                                            accountId={accountIdFromResolvedEntity(obj.row.original.product)}
                                        />
                                    </td>
                                )
                            ],
                            [
                                'categories',
                                (obj) => (
                                    <td className={defaultCellClassName}>
                                        {obj.row.original.categories?.length > 0 && (
                                            <>{obj.row.original.categories[0].name}</>
                                            // <PlatformObjectBadge
                                            //     entityType={EntityType.CATEGORY}
                                            //     display={obj.row.original.categories[0].name}
                                            //     id={obj.row.original.categories[0].entity_id}
                                            // />
                                        )}
                                    </td>
                                )
                            ]
                        ])
                    }}
                    type={EntityType.EQUIPMENT}
                />
            );
        case 'at_laundry':
            return (
                <ReactiveObjectList<LaundryEquipment>
                    fetch={(arg) =>
                        LaundryEquipmentClient.fetchLaundryEquipments(viewProps.currentAccount, { state: EquipmentStateName.EQUIPMENT_MAINTENANCE, ...arg })
                    }
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({
                            entityType: EntityType.EQUIPMENT,
                            id: obj.id,
                            context: { application: Application.LAUNDRY, feature: Feature.EQUIPMENTS }
                        })
                    }
                    id='laundry-items-list-at-laundry'
                    dispatch={viewProps.dispatch}
                    activeFilter={{
                        searchKeys: ['tag', 'serial_number', 'product', 'categories', 'user'],
                        filterKeys: ['users', 'update_timestamp'],
                        displayName: new Map([
                            ['serial_number', CommonMessage.OBJECTS.EQUIPMENT.SERIAL_NUMBER],
                            ['product', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                            ['categories', CommonMessage.OBJECTS.CATEGORY.DEFAULT],
                            ['user', CommonMessage.OBJECTS.USER.DEFAULT],
                            ['users', CommonMessage.OBJECTS.USER.PLURAL],
                            ['update_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.SINCE]
                        ]),
                        queryKeyMap: new Map([['users', 'user_id']])
                    }}
                    columns={{
                        columns: ['tag', 'serial_number', 'product', 'categories', 'user', 'update_timestamp'],
                        headerOverwrite: new Map([
                            ['serial_number', CommonMessage.OBJECTS.EQUIPMENT.SERIAL_NUMBER],
                            ['product', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                            ['categories', CommonMessage.OBJECTS.CATEGORY.DEFAULT],
                            ['user', CommonMessage.OBJECTS.USER.DEFAULT],
                            ['update_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.SINCE]
                        ]),
                        customCells: new Map([
                            ['tag', (obj) => <td className={defaultCellClassName}>{obj.row.original.tag?.name}</td>],
                            [
                                'product',
                                (obj) => (
                                    <td className={defaultCellClassName}>
                                        <PlatformObjectBadge
                                            entityType={EntityType.PRODUCT}
                                            display={obj.row.original.product.name}
                                            id={obj.row.original.product.entity_id}
                                            foreign={obj.row.original.product.foreign}
                                            accountId={accountIdFromResolvedEntity(obj.row.original.product)}
                                        />
                                    </td>
                                )
                            ],
                            [
                                'categories',
                                (obj) => (
                                    <td className={defaultCellClassName}>
                                        {obj.row.original.categories?.length > 0 && (
                                            <>{obj.row.original.categories[0].name}</>
                                            // <PlatformObjectBadge
                                            //     entityType={EntityType.CATEGORY}
                                            //     display={obj.row.original.categories[0].name}
                                            //     id={obj.row.original.categories[0].entity_id}
                                            // />
                                        )}
                                    </td>
                                )
                            ],
                            [
                                'user',
                                (obj) => (
                                    <td className={defaultCellClassName}>
                                        {obj.row.original.user && (
                                            <PlatformObjectBadge
                                                entityType={EntityType.USER}
                                                display={obj.row.original.user.name}
                                                id={obj.row.original.user.entity_id}
                                                key={obj.row.original.user.entity_id}
                                                foreign={obj.row.original.user.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.user)}
                                            />
                                        )}
                                    </td>
                                )
                            ]
                        ])
                    }}
                    type={EntityType.EQUIPMENT}
                />
            );
        case 'with_problems':
            return (
                <ReactiveObjectList<IEquipment>
                    fetch={(arg) => EquipmentClient.fetchEquipments({ account_id: viewProps.currentAccount, ...arg })}
                    useStaticListId
                    id='laundry-items-list-with-problems'
                    dispatch={viewProps.dispatch}
                    columns={{
                        columns: ['serial_number', 'serial_number', 'serial_number'],
                        headerOverwrite: new Map([['serial_number', 'ID']])
                    }}
                    type={EntityType.EQUIPMENT}
                />
            );
    }
};

const TagCell = (props: { obj: CellContext<LaundryEquipment, any> }) => {
    const [tag, setTag] = useState<string | undefined>(undefined);
    useEffect(() => {
        IdentificationClient.fetchIdentifications({
            account_id: props.obj.row.original.account_id,
            entity_type: EntityType.EQUIPMENT,
            entity_id: props.obj.row.original.id
        }).then((response) => {
            setTag(response.results[0]?.key);
        });
    }, [props.obj.row.original.account_id, props.obj.row.original.id]);
    return <td className={defaultCellClassName}>{tag}</td>;
};
